import { LitElement, html, css, classMap, styleMap } from '@hh/global.js'

class HHMegaMenuDrawer extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    currentMenu: {
      type: String,
      reflect: true,
      attribute: 'current-menu',
    },
    _currentFocusedMenu: {
      type: Object,
    },
  }

  constructor() {
    super()
    this.open = false
  }

  connectedCallback() {
    super.connectedCallback()
    this._boundCloseMegaMenu = this.closeMegaMenu.bind(this)
    this._boundHandleKeyDown = this.handleKeyDown.bind(this)
    this._boundHandleToggleKeydown = this.handleToggleKeydown.bind(this)
    this._boundHandleToggleMegaMenu = this.handleToggleMegaMenu.bind(this)
    this._boundScheduleClose = this.scheduleClose.bind(this)
    this._boundCancelScheduledClose = this.cancelScheduledClose.bind(this)

    this.overlay.forEach(el => {
      el.addEventListener('click', this._boundCloseMegaMenu)
    })

    window.addEventListener('toggleMegaMenu', this._boundHandleToggleMegaMenu)
    window.addEventListener('triggerHeaderSearch', this._boundCloseMegaMenu)

    window.addEventListener('scheduleMegaMenuClose', this._boundScheduleClose)
    this.addEventListener('mouseenter', this._boundCancelScheduledClose)
    this.addEventListener('mouseleave', this._boundScheduleClose)

    document.addEventListener('keydown', this._boundHandleKeyDown)
    this.toggleMenuButtons.forEach((button) => {
      button.addEventListener('keydown', this._boundHandleToggleKeydown)
    })

    this._boundHandleFocusIn = this.handleFocusIn.bind(this)
    document.addEventListener('focusin', this._boundHandleFocusIn)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.overlay.forEach(el => {
      el.removeEventListener('click', this._boundCloseMegaMenu)
    })
    window.removeEventListener('toggleMegaMenu', this._boundHandleToggleMegaMenu)
    window.removeEventListener('triggerHeaderSearch', this._boundCloseMegaMenu)
    window.removeEventListener('scheduleMegaMenuClose', this._boundScheduleClose)
    this.removeEventListener('mouseenter', this._boundCancelScheduledClose)
    this.removeEventListener('mouseleave', this._boundScheduleClose)
    document.removeEventListener('keydown', this._boundHandleKeyDown)
    this.toggleMenuButtons.forEach((button) => {
      button.removeEventListener('keydown', this._boundHandleToggleKeydown)
    })
    document.removeEventListener('focusin', this._boundHandleFocusIn)
  }

  handleToggleMegaMenu(event) {
    this._keyboardOpen = false
    if (event.detail.hoverOpen) {
      this.cancelScheduledClose()
      this.openMegaMenu(event.detail.clickEvent, true)
    } else {
      this.toggleMegaMenu(event.detail.clickEvent)
    }
  }

  // A hover-opened menu isn't focus-trapped (see openMegaMenu/setCurrentMenu
  // below), so Tab can carry focus straight out of it into the following
  // page content (e.g. the homepage banner). Close it once that happens,
  // rather than leaving it open and unreachable by keyboard.
  handleFocusIn(e) {
    if (!this.open || !this._openedViaHover) return
    if (this.contains(e.target)) return
    this.closeMegaMenu()
  }

  scheduleClose() {
    if (!this._openedViaHover) return
    this._closeTimer = setTimeout(() => this.closeMegaMenu(), 200)
  }

  cancelScheduledClose() {
    clearTimeout(this._closeTimer)
  }

  // Shared by both the click (component-header.js) and keyboard
  // (handleToggleKeydown below) paths: opens the clicked/activated menu, or
  // closes it if it's the one already open.
  toggleMegaMenu(e) {
    const target = e.target.closest('[data-menu-handle]') ?? e.target
    const { menuHandle } = target.dataset

    if (this.open && this.currentMenu === menuHandle) {
      this.closeMegaMenu()
    } else {
      this.openMegaMenu(e)
    }
  }

  firstUpdated() {
    setTimeout(() => {
      this.setTotalHeaderHeight()
      this.setDrawerHeight()
    }, 500)
  }

  updated(changedProperties) {
    if (changedProperties.has('open') && typeof changedProperties.get('open') !== 'undefined') {
      this.toggleOverlay()
      this.toggleBodyScroll()
      this.updateAriaHidden()
    }
    if (changedProperties.has('currentMenu')) {
      this.setCurrentMenu(this.currentMenu)
    }
  }

  updateAriaHidden() {
    const isHidden = !this.open;

    this.setAttribute('aria-hidden', String(isHidden));

    if (isHidden) {
      this.setAttribute('inert', '');
    } else {
      this.removeAttribute('inert');
    }
  }

  handleToggleKeydown(e) {
    if (e.code === 'Enter' || e.code === 'Space') {
      e.preventDefault()
      this._keyboardOpen = true
      this.toggleMegaMenu(e)
    }
  }

  // Open and close methods
  openMegaMenu(e, isHoverOpen = false) {
    const target = e.target.closest('[data-menu-handle]') ?? e.target
    this._currentFocusedMenu = target
    this._openedViaHover = isHoverOpen
    const { menuHandle } = target.dataset
    this.open = true
    this.currentMenu = menuHandle
    this.setTotalHeaderHeight()
    this.setDrawerHeight()

    this.dispatchEvent(new CustomEvent('mega-menu-open', { bubbles: true }))
  }

  closeMegaMenu() {
    this.open = false
    this.removeTrapFocus()
    this.currentMenu = ''
  }

  handleKeyDown(e) {
    if (e.key === 'Tab' || e.keyCode === 9) {
      this.revertHideFocus()
      this.handOverToKeyboard(e)
    }
    if (e.key !== 'Escape') return
    this.closeMegaMenu()
  }

  handOverToKeyboard(e) {
    if (!this.open || !this._openedViaHover || this.contains(e.target)) return
    const menu = this.submenus.find((el) => el.classList.contains('active'))
    if (!menu) return
    e.preventDefault()
    this._openedViaHover = false
    this._keyboardOpen = true
    this.cancelScheduledClose()
    this.addFocusTrap(menu)
  }

  revertHideFocus() {
    this.classList.remove('hide-focus')
    const buttonElement =
      this._currentFocusedMenu?.shadowRoot?.querySelector('button')
    buttonElement?.classList.remove('hide-focus')
  }

  toggleOverlay() {
    this.overlay.forEach(el => {
      el.classList[this.open ? 'add' : 'remove']('active')
    })
  }

  toggleBodyScroll() {
    // Locks scrolling via overflow:hidden on <html> rather than the old
    // "body.no-scroll" (position:fixed) trick - that approach detaches the
    // page from its scroll position entirely, which either visually resets
    // it to the top or (once compensated with a top offset) still reports
    // window.pageYOffset as 0 for an instant, both of which confused the
    // sticky header's scroll-position-driven expanded/reduced state.
    // overflow:hidden never touches scrollY at all: nothing to jump back
    // from, and no false "at the top" reading ever reaches the scroll
    // listener, so the reduced state simply can't be affected by this lock.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

    if (this.open) {
      document.documentElement.style.overflow = 'hidden'
      if (scrollbarWidth > 0) {
        document.documentElement.style.paddingRight = `${scrollbarWidth}px`
      }
    } else {
      document.documentElement.style.overflow = ''
      document.documentElement.style.paddingRight = ''
      setTimeout(() => {
        this.stickyHeader.externalRevealHeader()
      }, 0)
    }
  }

  setCurrentMenu(menuHandle) {
    let activeMenu = null

    this.submenus.forEach((menu) => {
      const megaMenuHandle = menu.dataset.megaMenuHandle
      menu.classList[megaMenuHandle === menuHandle ? 'add' : 'remove']('active')

      if (megaMenuHandle === menuHandle) {
        activeMenu = menu
        this.toggleNegativeTabIndex(menu)
        if (!this._openedViaHover) {
          this.addFocusTrap(menu)
        }
      }
    })

    this.navLinks.forEach((link) => {
      const linkMenuHandle = link.dataset.menuHandle
      link.classList[linkMenuHandle === menuHandle ? 'add' : 'remove']('active')
      link.setAttribute('aria-expanded', linkMenuHandle === menuHandle)
    })

    this.updateMenusWrapperHeight(activeMenu)
  }

  updateMenusWrapperHeight(activeMenu) {
    // Deferred to the next frame so measuring scrollHeight (a forced layout
    // reflow) doesn't compete with the opacity/transform open transition,
    // which was causing a visible stutter right as the dropdown appeared.
    requestAnimationFrame(() => {
      const menusWrapper = this.shadowRoot?.querySelector('.menus-wrapper')
      if (!menusWrapper) return
      menusWrapper.style.minHeight = activeMenu ? `${activeMenu.scrollHeight}px` : ''
    })
  }

  toggleNegativeTabIndex(menu) {
    const links = this.querySelectorAll('.js-submenu-link')
    links.forEach((link) => {
      if (menu.contains(link)) {
        link.removeAttribute('tabindex')
        link.removeAttribute('aria-hidden')
      } else {
        link.setAttribute('tabindex', '-1')
        link.setAttribute('aria-hidden', 'true')
      }
    })
  }

  addFocusTrap(menu) {
    this.classList.toggle('hide-focus', !this._keyboardOpen)
    const focusableElementsString =
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'

    const isUsable = (el) =>
      el &&
      el.getAttribute('tabindex') !== '-1' &&
      el.getAttribute('aria-hidden') !== 'true' &&
      el.getClientRects().length > 0

    this.focusableDomElements = this.querySelectorAll(focusableElementsString)
    this.focusablePromoElements =
      this.featuredPromos?.shadowRoot.querySelectorAll(focusableElementsString) ?? []

    this.closeButton = this.shadowRoot.querySelector('.close')

    this.focusableElements = [
      ...this.focusableDomElements,
      ...this.focusablePromoElements,
      this.closeButton,
    ].filter(isUsable)

    this.focusableElements[0]?.classList.add('focus-visible')
    this.firstFocusableElement = this.focusableElements[0]
    this.lastFocusableElement =
      this.focusableElements[this.focusableElements.length - 1]

    setTimeout(() => {
      this.firstFocusableElement?.focus()
    })
    this.currentMenuElement = menu

    this.removeEventListener('keydown', this._trapHandler)
    this._trapHandler = this.onTrapFocus.bind(this)
    this.addEventListener('keydown', this._trapHandler)
  }

  removeTrapFocus() {
    this.removeEventListener('keydown', this._trapHandler)
    const buttonElement =
      this._currentFocusedMenu?.shadowRoot?.querySelector('button')
    buttonElement?.focus()
    buttonElement?.classList.add('hide-focus')
  }

  onTrapFocus(e) {
    const isTabPressed = e.key === 'Tab' || e.keyCode === 9
    if (!isTabPressed || !this.open) return

    if (e.shiftKey) {
      if (
        document.activeElement === this.firstFocusableElement ||
        document.activeElement.shadowRoot?.activeElement ===
        this.firstFocusableElement
      ) {
        this.lastFocusableElement.focus()
        e.preventDefault()
      }
    } else {
      if (
        document.activeElement === this.lastFocusableElement ||
        document.activeElement.shadowRoot?.activeElement ===
        this.lastFocusableElement
      ) {
        this.firstFocusableElement.focus()
        e.preventDefault()
      }
    }
  }

  setTotalHeaderHeight() {
    HH.setCSSVariable('--hh-header-height', `${this.header.offsetHeight}px`)
    HH.setCSSVariable('--hh-announcement-bar-height', `${this.announcementBar.offsetHeight}px`)
    HH.setCSSVariable('--hh-total-header-height', `${this.totalHeaderHeight}px`)
    HH.setCSSVariable('--hh-fixed-total-header-height', `${this.totalHeaderHeight}px`)
  }

  setDrawerHeight() {
    HH.setCSSVariable('--hh-mega-menu-drawer-height', `${this.windowHeight}px`)
  }

  render() {
    return html`
      <div class="wrapper">
        <div class="menus-wrapper">
          <slot name="submenu"></slot>
        </div>
        <slot name="promos"></slot>
      </div>
      <button
        aria-label="Close"
        class="${classMap({
          close: true,
          active: this.open
        })}"
        @click=${this.closeMegaMenu}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.57129 6.3421L17.4284 17.1992" stroke="currentColor" stroke-width="1.25"/>
          <path d="M6.57129 17.1992L17.4284 6.3421" stroke="currentColor" stroke-width="1.25"/>
        </svg>
      </button>
      <slot name="overlay"></slot>
    `
  }

  static styles = css`
    :host {
      box-sizing: border-box;
    }

    *,
    *::before,
    *::after {
      box-sizing: inherit;
    }

    .wrapper {
      position: relative;
      z-index: 0;
      /* Match the header's own desktop horizontal padding (px-lg lg:px-2xl
         in sections/header.liquid = 32px at desktop, where this dropdown
         only ever shows) so the dropdown's edges align with the header's
         content instead of using an unrelated percentage. */
      padding: 32px 32px 54px;
      width: 100%;
      max-width: none;
      box-sizing: border-box;
      background-color: var(--hh-color-background);
    }

    .menus-wrapper {
      position: relative;
    }

    .close {
      display: none;
    }

    .close:focus-visible {
      outline: 0.1250rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }
  `

  // Getters
  get windowHeight() {
    return window.innerHeight
  }

  get totalHeaderHeight() {
    return this.header.offsetHeight + this.announcementBar.offsetHeight
  }

  get header() {
    return document.querySelector('.js-header') ?? null
  }

  get announcementBar() {
    return document.querySelector('.js-announcement-bar') ?? null
  }

  get overlay() {
    return document.querySelectorAll('.js-mega-menu-overlay') ?? null
  }

  get submenus() {
    const slot = this.shadowRoot.querySelector('slot[name="submenu"]')
    return slot.assignedElements({ flatten: true }) ?? null
  }

  get stickyHeader() {
    return document.querySelector('hh-sticky-header')
  }

  get navLinks() {
    const navLinks = document.querySelectorAll('[data-menu-handle]')
    if (!navLinks) return null
    return navLinks
  }

  get featuredPromos() {
    return document.querySelector('hh-menu-featured-promos') ?? null
  }

  get toggleMenuButtons() {
    const toggleButtons = document.querySelectorAll('.js-toggle-menu-button')
    if (!toggleButtons) return null
    return toggleButtons
  }
}

customElements.define('hh-mega-menu-drawer', HHMegaMenuDrawer)
